import React, { useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import classes from "./Testimonies.module.scss";
import CustomImage from "../../CustomImage";
import PlayIcon from "../../UI/Icons/PlayIcon";
import Quote from "../../UI/Icons/Quote";
import Modal from "../../UI/Modal/Modal";
import useIsMobile from "../../../hooks/useIsMobile";
import { convertYouTubeToEmbed } from "../../../utils/helpers";
import Chevron from "../../UI/Icons/Chevron";

interface ImageAsset {
  url: string;
  name?: string;
  alternativeText?: string;
}

interface Testimony {
  id: number;
  statement: string;
  name: string;
  designation: string;
  videoLink?: string;
  profileImg?: ImageAsset;
}

interface TestimoniesData {
  title: string;
  testimony: Testimony[];
}

interface Props {
  testimoniesData?: TestimoniesData;
  onPlayClick?: (videoLink: string) => void;
}

// The design hides the play button on the second slide regardless of whether that testimony has a videoLink.
const PLAY_BUTTON_HIDDEN_INDEX = 1;

interface TestimonyCardProps {
  testimony: Testimony;
  index: number;
  onPlayClick?: (videoLink: string) => void;
  onOpenVideoModal: (videoLink: string) => void;
  isMobile: boolean;
  isActive: boolean;
}

const AUTO_FLIP_DELAY_MS = 500;

const TestimonyCard = ({ testimony, index, onPlayClick, onOpenVideoModal, isMobile, isActive }: TestimonyCardProps) => {
  const [isFlipped, setIsFlipped] = useState(() => !isMobile && index === 0);

  useEffect(() => {
    if (!isMobile) return;

    if (!isActive) {
      setIsFlipped(false);
      return;
    }

    const timer = setTimeout(() => setIsFlipped(true), AUTO_FLIP_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isActive, isMobile]);

  const handleFrontPlayClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (testimony.videoLink) onPlayClick?.(testimony.videoLink);
  };

  const handleBackPlayClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (testimony.videoLink) onOpenVideoModal(testimony.videoLink);
  };

  const renderPlayButton = ({ theme = "white", onClick }: { theme?: "white" | "golden"; onClick: (e: React.MouseEvent<HTMLButtonElement>) => void }) => (
    <>
      {theme === "golden" ? (
        <div className={classes["outer-radius"]} style={{ background: "#ffffff45" }}>
          <button
            type="button"
            className={classes["play-button"]}
            onClick={onClick}
            aria-label={`Play ${testimony.name}'s testimony video`}
            style={{ backgroundColor: "#ffffff" }}
          >
            <PlayIcon color="#F5A623" width={isMobile ? 10 : 16} height={isMobile ? 10 : 16} />
          </button>
        </div>
      ) : (
        <div className={classes["outer-radius"]}>
          <button type="button" className={classes["play-button"]} onClick={onClick} aria-label={`Play ${testimony.name}'s testimony video`}>
            <PlayIcon color="#ffffff" width={isMobile ? 10 : 15} height={isMobile ? 10 : 15} />
          </button>
        </div>
      )}
    </>
  );

  return (
    <div
      className={`${classes["flip-card"]} ${isFlipped ? classes["flip-card--flipped"] : ""}`.trim()}
      onMouseEnter={() => setIsFlipped(true)}
      onMouseLeave={() => setIsFlipped(false)}
      onClick={() => setIsFlipped((prev) => !prev)}
    >
      <div className={classes["flip-card-inner"]}>
        <div className={classes["flip-card-front"]}>
          <div className={classes["flip-card-overlay"]}></div>
          <CustomImage src={testimony.profileImg?.url ?? ""} alt={testimony.profileImg?.name ?? testimony.name} layout="fill" objectFit="cover" />
          <div className={classes["front-overlay"]}>
            <div>
              <p className={classes["testimony-name"]}>{testimony.name}</p>
              <div className={classes["testimony-designation"]} dangerouslySetInnerHTML={{ __html: testimony.designation }} />
            </div>
            {testimony.videoLink && renderPlayButton({ theme: "golden", onClick: handleFrontPlayClick })}
          </div>
        </div>

        <div className={classes["flip-card-back"]}>
          <Quote />
          <div className={classes["testimony-statement"]}>
            <div className={classes["testimony-statement-text"]} dangerouslySetInnerHTML={{ __html: testimony.statement }} />
          </div>
          <div className={classes["back-footer"]}>
            <div>
              <p className={classes["testimony-name"]}>{testimony.name}</p>
              <div className={classes["testimony-designation"]} dangerouslySetInnerHTML={{ __html: testimony.designation }} />
            </div>
            {testimony.videoLink && renderPlayButton({ theme: "white", onClick: handleBackPlayClick })}
          </div>
        </div>
      </div>
    </div>
  );
};

const Testimonies = ({ testimoniesData, onPlayClick }: Props) => {
  const isMobile = useIsMobile(1024);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: "start" });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(true);
  const [activeVideoLink, setActiveVideoLink] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSectionInView, setIsSectionInView] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(([entry]) => setIsSectionInView(entry.isIntersecting), { threshold: 0.3 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    const updateScrollState = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
      setHasOverflow(emblaApi.canScrollPrev() || emblaApi.canScrollNext());
      setActiveIndex(emblaApi.selectedScrollSnap());
    };

    updateScrollState();
    emblaApi.on("select", updateScrollState);
    emblaApi.on("reInit", updateScrollState);
    emblaApi.on("resize", updateScrollState);

    return () => {
      emblaApi.off("select", updateScrollState);
      emblaApi.off("reInit", updateScrollState);
      emblaApi.off("resize", updateScrollState);
    };
  }, [emblaApi, testimoniesData]);

  if (!testimoniesData) return null;

  const { title, testimony } = testimoniesData;

  const goToPrev = () => emblaApi?.scrollPrev();
  const goToNext = () => emblaApi?.scrollNext();
  const closeVideoModal = () => setActiveVideoLink(null);

  const activeVideoEmbedUrl = (() => {
    const embedUrl = convertYouTubeToEmbed(activeVideoLink);
    if (!embedUrl) return null;
    return `${embedUrl}${embedUrl.includes("?") ? "&" : "?"}autoplay=1`;
  })();

  return (
    <section ref={sectionRef} className={classes["testimonies-container"]}>
      {title && <h2 className={classes.heading}>{title}</h2>}

      <div className={classes["carousel-wrapper"]}>
        {!isMobile && hasOverflow && (
          <button
            type="button"
            className={`${classes.btcircle}${canScrollPrev ? "" : ` ${classes["btcircle-disabled"]}`} ${classes.btnCircleLeft}`}
            onClick={canScrollPrev ? goToPrev : undefined}
            aria-disabled={!canScrollPrev}
            aria-label="Previous testimonial"
          >
            <Chevron width={14} height={14} />
          </button>
        )}

        <div className={classes.viewport} ref={emblaRef}>
          <div className={`${classes["slider-container"]}${hasOverflow ? "" : ` ${classes["slider-container--centered"]}`}`}>
            {testimony?.length > 0 &&
              testimony?.map((item, index) => (
                <div key={item.id} className={classes.slide}>
                  <TestimonyCard
                    testimony={item}
                    index={index}
                    onPlayClick={onPlayClick}
                    onOpenVideoModal={setActiveVideoLink}
                    isMobile={isMobile}
                    isActive={isMobile && index === activeIndex && isSectionInView}
                  />
                </div>
              ))}
          </div>
        </div>

        {!isMobile && hasOverflow && (
          <button
            type="button"
            className={`${classes.btcircle}${canScrollNext ? "" : ` ${classes["btcircle-disabled"]}`}`}
            onClick={canScrollNext ? goToNext : undefined}
            aria-disabled={!canScrollNext}
            aria-label="Next testimonial"
          >
            <Chevron width={14} height={14} />
          </button>
        )}

        {isMobile && hasOverflow && (
          <div className={classes["nav-arrows"]}>
            <button
              type="button"
              className={`${classes.btcircle}${canScrollPrev ? "" : ` ${classes["btcircle-disabled"]}`} ${classes.btnCircleLeft}`}
              onClick={canScrollPrev ? goToPrev : undefined}
              aria-disabled={!canScrollPrev}
              aria-label="Previous testimonial"
            >
              <Chevron width={14} height={14} />
            </button>
            <button
              type="button"
              className={`${classes.btcircle}${canScrollNext ? "" : ` ${classes["btcircle-disabled"]}`}`}
              onClick={canScrollNext ? goToNext : undefined}
              aria-disabled={!canScrollNext}
              aria-label="Next testimonial"
            >
              <Chevron width={14} height={14} />
            </button>
          </div>
        )}
      </div>

      <Modal isOpen={!!activeVideoLink} onClose={closeVideoModal} contentClassName={classes["video-modal-container"]}>
        {activeVideoEmbedUrl && (
          <iframe
            src={activeVideoEmbedUrl}
            className={classes["modal-video"]}
            title="Testimonial video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )}
      </Modal>
    </section>
  );
};

export default Testimonies;
